/**
 * Public Routes Configuration
 * Routes accessible without authentication
 */
import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { PUBLIC_ROUTES } from './constants';

const RedirectToConsultation = () => <Navigate to="/our-services/online-consultation" replace />;
const RedirectToFactories = () => <Navigate to="/vastu-knowledge/advance-topics?tab=factories" replace />;
const RedirectToHomes = () => <Navigate to="/vastu-knowledge/advance-topics?tab=homes" replace />;

// Lazy load components
const HomePage = lazy(() => import('@/pages/static/Home/HomePage'));
const VastuKnowledgePage = lazy(() => import('@/pages/static/VastuKnowledge/VastuKnowledgePage'));
const TransformativePowerPage = lazy(() => import('@/pages/static/VastuKnowledge/TransformativePowerPage'));
const VastumandalaPage = lazy(() => import('@/pages/static/VastuKnowledge/VastumandalaPage'));
const AdvanceVastuTopicsPage = lazy(() => import('@/pages/static/VastuKnowledge/AdvanceVastuTopicsPage'));
const AyadiCalculationPage = lazy(() => import('@/pages/static/VastuKnowledge/AyadiCalculationPage'));
const PlantsVastuPage = lazy(() => import('@/pages/static/VastuKnowledge/PlantsVastuPage'));
const ConsecrateSpacePage = lazy(() => import('@/pages/static/VastuKnowledge/ConsecrateSpacePage'));
const HarmfulEnergiesPage = lazy(() => import('@/pages/static/VastuKnowledge/HarmfulEnergiesPage'));
const OurServicesPage = lazy(() => import('@/pages/static/OurServices/OurServicesPage'));
const VastuConsultancyPage = lazy(() => import('@/pages/static/OurServices/VastuConsultancyPage'));
const RectificationPage = lazy(() => import('@/pages/static/OurServices/RectificationPage'));
const OnlineConsultationPage = lazy(() => import('@/pages/static/OurServices/OnlineConsultationPage'));
const ArchitecturalReviewPage = lazy(() => import('@/pages/static/OurServices/ArchitecturalReviewPage'));
const OnsiteServicesPage = lazy(() => import('@/pages/static/OurServices/OnsiteServicesPage'));
const OnlineVastuSelfPage = lazy(() => import('@/pages/static/OnlineVastuSelf/OnlineVastuSelfPage'));
const VastuCoursesPage = lazy(() => import('@/pages/static/VastuCourses/VastuCoursesPage'));
const BlogPage = lazy(() => import('@/pages/static/Blog/BlogPage'));
const FaqsPage = lazy(() => import('@/pages/static/Faqs/FaqsPage'));
const ContactUsPage = lazy(() => import('@/pages/static/ContactUs/ContactUsPage'));

const LandAssessmentPage = lazy(() => import('@/pages/assessment/LandAssessmentPage'));
const EnvironmentAssessmentPage = lazy(() => import('@/pages/assessment/EnvironmentAssessmentPage'));
const FlatAssessmentPage = lazy(() => import('@/pages/assessment/FlatAssessmentPage'));
const HouseAssessmentPage = lazy(() => import('@/pages/assessment/HouseAssessmentPage'));

/**
 * Public route configuration array
 */
export const publicRoutes = [
  {
    path: PUBLIC_ROUTES.HOME,
    component: HomePage,
    name: 'Home',
    meta: {
      title: 'Home - VastuSindu',
      description: 'Welcome to VastuSindu',
    },
  },

  {
    path: '/vastu-knowledge/transformative-power',
    component: TransformativePowerPage,
    name: 'The Transformative Power of Vāstuśāstra',
    meta: {
      title: 'The Transformative Power of Vāstuśāstra - VastuSindu',
      description: 'Discover the transformative power of Vastu Shastra',
    },
  },
  {
    path: '/vastu-knowledge/vastumandala',
    component: VastumandalaPage,
    name: 'Vāstupuruṣamaṇḍala',
    meta: {
      title: 'Vāstupuruṣamaṇḍala: The Universe in a Square - VastuSindu',
      description: 'Learn about the sacred Vāstupuruṣamaṇḍala',
    },
  },
  {
    path: '/vastumandala',
    component: VastumandalaPage,
    name: 'Vastumandala',
    meta: {
      title: 'Vāstupuruṣamaṇḍala: The Universe in a Square - VastuSindu',
      description: 'Learn about the sacred Vāstupuruṣamaṇḍala',
    },
  },
  {
    path: '/vastu-knowledge/advance-topics',
    component: AdvanceVastuTopicsPage,
    name: 'Advance Vastu Topics',
    meta: {
      title: 'Advance Vastu Topics - VastuSindu',
      description: 'Explore advanced Vastu Shastra topics',
    },
  },
  {
    path: '/vastu-knowledge/ayadi-calculation',
    component: AyadiCalculationPage,
    name: 'Ayadi Calculation',
    meta: {
      title: 'Ayadi Calculation - VastuSindu',
      description: 'Understanding Ayadi calculations in Vastu',
    },
  },
  {
    path: '/ayadi-calculations',
    component: AyadiCalculationPage,
    name: 'Ayadi Calculation',
    meta: {
      title: 'Ayadi Calculations in Vastu Shastra - VastuSindu',
      description: 'Understanding Ayadi calculations in Vastu',
    },
  },
  {
    path: '/vastu-knowledge/plants',
    component: PlantsVastuPage,
    name: 'Plants and Trees in Vāstuśāstra',
    meta: {
      title: 'Plants and Trees in Vāstuśāstra - VastuSindu',
      description: 'Learn about plants and trees in Vastu Shastra',
    },
  },
  {
    path: '/vastu-plants-and-trees',
    component: PlantsVastuPage,
    name: 'Plants and Trees in Vāstuśāstra',
    meta: {
      title: 'Plants and Trees in Vāstuśāstra - VastuSindu',
      description: 'Learn about plants and trees in Vastu Shastra',
    },
  },
  {
    path: '/vastu-knowledge/consecrate-space',
    component: ConsecrateSpacePage,
    name: 'Consecrate Your Space',
    meta: {
      title: 'Consecrate Your Space - VastuSindu',
      description: 'Learn how to consecrate your living space',
    },
  },
  {
    path: '/vastu-knowledge/harmful-energies',
    component: HarmfulEnergiesPage,
    name: 'Symptoms of Harmful Vastu Energies',
    meta: {
      title: 'Symptoms of Harmful Vastu Energies - VastuSindu',
      description: 'Recognize symptoms of harmful Vastu energies',
    },
  },
  {
    path: '/vastu-dosh-symptoms',
    component: HarmfulEnergiesPage,
    name: 'Symptoms of Harmful Vastu Energies',
    meta: {
      title: 'Symptoms of Harmful Vastu Energies - VastuSindu',
      description: 'A guide to recognising Negative Vastu Energies (NVE)',
    },
  },
  {
    path: '/our-services',
    component: OurServicesPage,
    name: 'My Services',
    meta: {
      title: 'My Services - VastuSindu',
      description: 'Explore our Vastu consultation services',
    },
  },
  {
    path: '/our-services/online-consultation',
    component: OnlineConsultationPage,
    name: 'Online Vastu Consultation',
    meta: {
      title: 'Online Vastu Consultation - VastuSindu',
      description: 'Book online Vastu consultation with experts',
    },
  },
  {
    path: '/our-services/architectural-review',
    component: ArchitecturalReviewPage,
    name: 'Online Vastu Review of Architectural Plans',
    meta: {
      title: 'Online Vastu Review of Architectural Plans - VastuSindu',
      description: 'Expert online Vastu assessment of architectural drawings, blueprints, and layouts.',
    },
  },
  {
    path: '/online-review-of-architectural-plans',
    component: ArchitecturalReviewPage,
    name: 'Online Vastu Review of Architectural Plans',
    meta: {
      title: 'Online Vastu Review of Architectural Plans - VastuSindu',
      description: 'Expert online Vastu assessment of architectural drawings, blueprints, and layouts.',
    },
  },
  {
    path: '/our-services/onsite-consultation',
    component: OnsiteServicesPage,
    name: 'Onsite Services',
    meta: {
      title: 'Onsite Services - VastuSindu',
      description: 'In-person on-site Vastu consultation, examination, and non-destructive rectification.',
    },
  },
  {
    path: '/onsite-services',
    component: OnsiteServicesPage,
    name: 'Onsite Services',
    meta: {
      title: 'Onsite Services - VastuSindu',
      description: 'In-person on-site Vastu consultation, examination, and non-destructive rectification.',
    },
  },
  {
    path: '/our-services/onsite-services',
    component: OnsiteServicesPage,
    name: 'Onsite Services',
    meta: {
      title: 'Onsite Services - VastuSindu',
      description: 'In-person on-site Vastu consultation, examination, and non-destructive rectification.',
    },
  },
  {
    path: '/online-vastu-self',
    component: OnlineVastuSelfPage,
    name: 'Online Vastu Self',
    meta: {
      title: 'Online Vastu Self - VastuSindu',
      description: 'Self-paced Vastu learning platform',
    },
  },
  {
    path: '/online-vastu-self-assessment',
    component: OnlineVastuSelfPage,
    name: 'Online Vastu Self Assessment',
    meta: {
      title: 'Online Vastu Self Assessment - VastuSindu',
      description: 'Online self-assessment for your plot, flat, or house',
    },
  },
  {
    path: '/vastu-courses',
    component: VastuCoursesPage,
    name: 'Vastu Courses',
    meta: {
      title: 'Vastu Courses - VastuSindu',
      description: 'Enroll in professional Vastu courses',
    },
  },
  {
    path: '/blog',
    component: BlogPage,
    name: 'Blog',
    meta: {
      title: 'Blog - VastuSindu',
      description: 'Read our latest Vastu articles',
    },
  },
  {
    path: '/blog/:slug',
    component: BlogPage,
    name: 'Blog Details',
    meta: {
      title: 'Blog Details - VastuSindu',
      description: 'Read full blog content',
    },
  },
  {
    path: '/faqs',
    component: FaqsPage,
    name: 'FAQs',
    meta: {
      title: 'FAQs - VastuSindu',
      description: 'Frequently asked questions about Vastu',
    },
  },
  {
    path: '/contact-us',
    component: ContactUsPage,
    name: 'Contact Us',
    meta: {
      title: 'Contact Us - VastuSindu',
      description: 'Get in touch with us',
    },
  },
  {
    path: '/vastu/factories',
    component: RedirectToFactories,
    name: 'Vastu for Factories',
  },
  {
    path: '/vastu/homes',
    component: RedirectToHomes,
    name: 'Vastu for Homes',
  },
  {
    path: '/vastu/hospitals',
    component: RedirectToConsultation,
    name: 'Vastu for Hospitals',
  },
  {
    path: '/vastu/shops',
    component: RedirectToConsultation,
    name: 'Vastu for Shops',
  },
  {
    path: '/vastu/townships',
    component: RedirectToConsultation,
    name: 'Vastu for Townships',
  },
  {
    path: '/vastu/lagna-mandap',
    component: RedirectToConsultation,
    name: 'Vastu and the Lagna Mandap',
  },
  {
    path: '/vastu/childrens-room',
    component: RedirectToConsultation,
    name: 'Vastu for Childrens Room',
  },
  {
    path: '/vastu/educational',
    component: RedirectToConsultation,
    name: 'Vastu for Educational Institutes',
  },
  {
    path: '/vastu/architecture',
    component: RedirectToConsultation,
    name: 'Vastu Architecture',
  },
  {
    path: '/assessment/land',
    component: LandAssessmentPage,
    name: 'Land Vastu Assessment',
    meta: {
      title: 'Land Vastu Assessment - VastuSindu',
      description: 'Self assess your land vastu',
    },
  },
  {
    path: '/assessment/flat',
    component: FlatAssessmentPage,
    name: 'Flat Assessment',
    meta: {
      title: 'Flat Assessment - VastuSindu',
      description: 'Flat Vastu Assessment',
    },
  },
  {
    path: '/assessment/house',
    component: HouseAssessmentPage,
    name: 'House Vastu Assessment',
    meta: {
      title: 'House Vastu Assessment - VastuSindu',
      description: 'Self assess your house vastu',
    },
  },
];

export default publicRoutes;
